import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { RefreshCw } from 'lucide-react';

const DailySummary = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => { fetchData(); }, [selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/production/daily-summary?date=${selectedDate}`);
      setData(r.data);
    } catch(e) { console.error(e); } finally { setLoading(false); }
  };

  const renderGroup = (title, groupData) => {
    const keys = Object.keys(groupData || {});
    if (keys.length === 0) return <p style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>No data</p>;
    return (
      <div className="table-container">
        <table>
          <thead><tr><th>{title}</th><th>Good</th><th>Rejected</th><th>Scrap</th><th>Rework</th><th>Total</th><th>Yield %</th></tr></thead>
          <tbody>
            {keys.map(k => {
              const d = groupData[k];
              const total = d.good + d.rejected + d.scrap + d.rework;
              const yieldPct = total > 0 ? ((d.good / total) * 100).toFixed(1) : '0.0';
              return (
                <tr key={k}>
                  <td style={{ fontWeight: 500 }}>{k}</td>
                  <td className="tabular-nums" style={{ color: '#059669' }}>{d.good}</td>
                  <td className="tabular-nums" style={{ color: '#DC2626' }}>{d.rejected}</td>
                  <td className="tabular-nums" style={{ color: '#D97706' }}>{d.scrap}</td>
                  <td className="tabular-nums">{d.rework}</td>
                  <td className="tabular-nums font-semibold">{total}</td>
                  <td className="tabular-nums">{yieldPct}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div data-testid="daily-summary-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Daily Production Summary</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-tertiary)', fontSize: '13px' }}>Reporting Layer - Production breakdown by shift, machine, and product</p>
        </div>
        <div className="flex gap-2 items-center">
          <input type="date" className="form-input" style={{ width: '180px' }} value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} data-testid="date-picker" />
          <button onClick={fetchData} className="btn btn-secondary btn-sm" data-testid="refresh-btn"><RefreshCw size={14} /></button>
        </div>
      </div>

      {loading ? <div className="card"><div style={{ padding: '20px' }}>Loading...</div></div> : !data ? <div className="card"><div style={{ padding: '20px', color: 'var(--text-tertiary)' }}>No data available</div></div> : (
        <>
          <div className="grid grid-cols-3 mb-4">
            <div className="stat-card" data-testid="stat-date">
              <div className="stat-value" style={{ fontSize: '20px' }}>{data.date}</div>
              <div className="stat-label">Report Date</div>
            </div>
            <div className="stat-card" data-testid="stat-entries">
              <div className="stat-value">{data.total_entries}</div>
              <div className="stat-label">Total Entries</div>
            </div>
            <div className="stat-card" data-testid="stat-total-output">
              <div className="stat-value">
                {Object.values(data.by_product || {}).reduce((s, d) => s + d.good + d.rejected + d.scrap + d.rework, 0)}
              </div>
              <div className="stat-label">Total Output</div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header"><h3 className="card-title">By Shift</h3></div>
            {renderGroup('Shift', data.by_shift)}
          </div>

          <div className="card mb-4">
            <div className="card-header"><h3 className="card-title">By Machine</h3></div>
            {renderGroup('Machine', data.by_machine)}
          </div>

          <div className="card mb-4">
            <div className="card-header"><h3 className="card-title">By Product</h3></div>
            {renderGroup('Product', data.by_product)}
          </div>

          <div className="card">
            <div className="card-header"><h3 className="card-title">Rejections — Batch-wise (for tracking &amp; reference)</h3></div>
            {(data.rejections || []).length === 0 ? (
              <p style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>No rejections recorded for this date</p>
            ) : (
              <div className="table-container">
                <table>
                  <thead><tr><th>Batch #</th><th>Product</th><th>Shift</th><th>Machine</th><th>Rejected Qty</th><th>Rejection Reason</th></tr></thead>
                  <tbody>
                    {data.rejections.map((r, i) => (
                      <tr key={i} data-testid={`rejection-row-${i}`}>
                        <td className="font-mono text-xs">{r.batch_number}</td>
                        <td>{r.product_name || r.product_sku || '-'}</td>
                        <td>{r.shift_name || '-'}</td>
                        <td>{r.machine_name || '-'}</td>
                        <td className="tabular-nums" style={{ color: '#DC2626' }}>{r.reject_qty}</td>
                        <td>{r.rejection_reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DailySummary;
